"use client";
import type { Resolver } from "react-hook-form";

import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { ProgramScheduleType } from "../../../../types/dto/program";
import { programPaymentScheduleAcademy } from "../../../../shared/services";
import { message } from "antd";
import { resolveScheduleMonthDate } from "../../utils/scheduleDateUtils";

type BulkSchedulePricesForm = Pick<
  ProgramScheduleType,
  "price" | "price_with_prompt_payment_discount"
>;

export interface UseBulkUpdateProgramsScheduleProps {
  programSchedules: ProgramScheduleType[];
  onSuccess?: () => void;
  setOpenModal: (value: boolean) => void;
}

function getCommonValue(
  schedules: ProgramScheduleType[],
  field: "price" | "price_with_prompt_payment_discount"
): string {
  if (!schedules.length) return "";

  const firstValue = schedules[0][field];
  const allMatch = schedules.every((schedule) => schedule[field] == firstValue);

  return allMatch && firstValue != null ? `${firstValue}` : "";
}

export default function useBulkUpdateProgramsSchedule({
  onSuccess,
  programSchedules,
  setOpenModal,
}: UseBulkUpdateProgramsScheduleProps) {
  const [loading, setLoading] = React.useState(false);

  const schema = yup.object({
    price: yup.string().required("Campo Requerido"),
    price_with_prompt_payment_discount: yup
      .string()
      .required("Campo Requerido"),
  });

  const {
    control,
    handleSubmit: onSubmit,
    reset,
  } = useForm<BulkSchedulePricesForm>({
    resolver: yupResolver(schema) as unknown as Resolver<BulkSchedulePricesForm>,
  });

  const handleSubmit = onSubmit(async (data) => {
    if (loading || !programSchedules.length) return;

    setLoading(true);

    try {
      await Promise.all(
        programSchedules.map(async (schedule) => {
          const payload: Record<string, number | string> = {
            price: +data.price,
            price_with_prompt_payment_discount:
              +data.price_with_prompt_payment_discount,
            program_id: +schedule.program_id,
          };

          const monthDate = resolveScheduleMonthDate(
            schedule.month,
            schedule.day_of_month_payment_limit
          );

          if (!moment(schedule.month).isValid() && monthDate.isValid()) {
            payload.month = monthDate.format("YYYY-MM-DD");
          }

          return programPaymentScheduleAcademy.patch(schedule.id, payload);
        })
      );

      message.success(
        `Se actualizaron ${programSchedules.length} cronogramas de pago`
      );
      setOpenModal(false);
      onSuccess?.();
    } catch (err: any) {
      message.error(`Algo salió mal: ${err.message}`);
    } finally {
      setLoading(false);
    }
  });

  React.useEffect(() => {
    reset({
      price: getCommonValue(programSchedules, "price"),
      price_with_prompt_payment_discount: getCommonValue(
        programSchedules,
        "price_with_prompt_payment_discount"
      ),
    });
  }, [programSchedules, reset]);

  return {
    control,
    loading,
    handleSubmit,
  };
}
